import type { SelectedFileStatus } from '@vizit/atoms';
import { createContext, useContext } from 'react';

/**
 * Function type for uploading a file with progress callback
 */
export type UploadFunction = (onProgress: (progress: number) => void) => Promise<void>;

/**
 * Input type for adding files to the upload queue
 */
export interface UploadQueueFileInput {
  /**
   * The file being uploaded
   * @type {File}
   */
  file: File;
  /**
   * Function to handle the upload for this specific file
   * @type {UploadFunction}
   */
  onUpload: UploadFunction;
}

/**
 * Internal data stored for each queue item
 */
export interface UploadQueueItemData {
  /**
   * Unique identifier for the queue item
   * @type {string}
   */
  id: string;
  /**
   * The file being uploaded
   * @type {File}
   */
  file: File;
  /**
   * Current upload progress (0-100)
   * @type {number}
   * @default 0
   */
  progress: number;
  /**
   * Current status of the upload
   * @type {SelectedFileStatus}
   * @default SelectedFileStatus.Ready
   */
  status: SelectedFileStatus;
  /**
   * Error message if upload failed
   * @type {string}
   * @optional
   */
  error?: string;
  /**
   * Function to handle the upload for this specific file
   * @type {UploadFunction}
   */
  onUpload: UploadFunction;
}

/**
 * Context type for the upload queue
 */
export interface UploadQueueContextType {
  /**
   * Current items in the upload queue
   * @type {UploadQueueItemData[]}
   */
  items: UploadQueueItemData[];
  /**
   * Whether any uploads are currently in progress
   * @type {boolean}
   */
  isUploading: boolean;
  /**
   * Add files to the upload queue with their upload handlers
   * @param files - Files with upload functions to add to the queue
   */
  onAddFiles: (files: UploadQueueFileInput[]) => void;
  /**
   * Remove an item from the queue
   * @param id - ID of the item to remove
   */
  onRemoveItem: (id: string) => void;
  /**
   * Clear all completed/failed items from the queue
   */
  onClearCompleted: () => void;
}

export const UploadQueueContext = createContext<UploadQueueContextType | undefined>(undefined);

/**
 * Hook to access the upload queue context
 * @throws Error if used outside of UploadQueueProvider
 * @returns {UploadQueueContextType} The upload queue context
 */
export function useUploadQueue(): UploadQueueContextType {
  const context = useContext(UploadQueueContext);
  if (!context) {
    throw new Error('useUploadQueue must be used within an UploadQueueProvider');
  }
  return context;
}
